/**
 * Histórico da função operacional de uma pessoa, embaixo do formulário.
 *
 * Responde ao requisito de auditoria: cada período com data de início e de
 * término, a função anterior, a nova e quem alterou. É a prova, na tela, de que a
 * troca de hoje não reescreveu o que a jornada registrou ontem.
 *
 * Só aparece na EDIÇÃO. Numa ficha nova não há período nenhum, e um card vazio
 * anunciando ausência de histórico é ruído.
 */
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import { http } from '../../core/api/client'
import type { PersonnelFuncaoPeriodo } from '../../core/api/types'
import { StatusChip } from '../../shared/components/StatusChip'
import { useDeclarativeText } from '../../shared/i18n/declarativeText'

const dia = (iso: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR') : null)

const instante = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : null

export function FuncaoHistorico({ editing }: { editing: boolean }) {
  const { uuid } = useParams<{ uuid: string }>()
  const tt = useDeclarativeText()
  const q = useQuery({
    queryKey: ['personnel', uuid, 'funcoes'],
    queryFn: () => http.get<PersonnelFuncaoPeriodo[]>(`/personnel/${uuid}/funcoes`),
    enabled: Boolean(editing && uuid),
  })
  const linhas = q.data ?? []
  if (!editing || !uuid || linhas.length === 0) return null

  return (
    <Card sx={{ p: { xs: 2.5, sm: 3 }, display: 'grid', gap: 2 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {tt('Histórico de função')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tt('A jornada de cada dia guarda a função que valia naquela data')}
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {linhas.map((linha) => (
          <Box
            key={linha.uuid}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 1fr) minmax(160px, 1.2fr) minmax(140px, 1fr) minmax(140px, 1fr)' },
              gap: 2,
              alignItems: 'start',
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {tt('Função')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600}>{linha.funcao_label}</Typography>
                {linha.vigente_ate === null && <StatusChip label="Atual" tone="success" />}
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {tt('Período')}
              </Typography>
              <Typography variant="body2">
                {tt('Início')} {dia(linha.vigente_de)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {linha.vigente_ate ? `${tt('Término')} ${dia(linha.vigente_ate)}` : tt('Sem término')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {tt('Função anterior')}
              </Typography>
              <Typography variant="body2">{linha.funcao_anterior_label ?? tt('Primeira função')}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {tt('Alterado por')}
              </Typography>
              <Typography variant="body2">{linha.autor ?? tt('Sincronização')}</Typography>
              <Typography variant="body2" color="text.secondary">{instante(linha.changed_at)}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  )
}
